import React from 'react'
import classnames from 'classnames'
import styled from 'styled-components'
import { MdClose } from 'react-icons/md'
import Thumb from '../../images/robot3Small.png'
import { useDimensions } from '../../utility/hooks.jsx'

const TOTAL_VOTES = 7 // The total number of votes possible

const THUMB_SIZE = 32 // Size of the image that will be the thumb on the range slider
/**
 * A range component that allows the user to vote how useful something is.
 * The user may provide a vote between -3 and 3, (7 different levels of usefulness)
 * onSelect is called when the user releases the slider.
 * onClose is called when the user clicks the close button.
 */
export function Voting({
	className,
	vote,
	onClose,
	onSelect,
	score,
}: {
	className?: string,
	vote: number | null,
	onClose: () => void,
	onSelect: (vote: number, location: { x: number, y: number }) => mixed,
	score: number,
}): React$Node {
	const [ref, dimensions] = useDimensions()
	// In order to give the effect of a smooth slider, we need to scale so that each unit on the slider is a pixel on the screen.
	const widthOfInput: number = dimensions?.width ?? 540 // width of the range input element (used to determine min and max ranges to create a smooth experience.
	const smoothnessMultiplier: number = widthOfInput / (TOTAL_VOTES - 1) // Used to translate the vote options into values on the range bar. See below for a description:
	/**
	 * There are 7 ticks on the slider:
	 *  -3|      -2|    -1|    0|    1|    2|     3
	 * Suppose the slider is 240px wide:
	 *  -120px   -80   -40     0	  40	80	  120px
	 *
	 * If input value is 40 then 40 / smoothnessMultiplier = 1
	 * Solving for the smoothnessMultiplier = 40 = (240 / 6)
	 * Therefore smoothness multiplier =  widthOfInput / (numTicks - 1)
	 */

	const [value, setValue] = React.useState((vote ?? 0) * smoothnessMultiplier)

	const sendValue = React.useCallback(() => {
		const vote = Math.round(value / smoothnessMultiplier)
		setValue(vote * smoothnessMultiplier)
		onSelect(vote, {
			x: dimensions?.x ?? 0,
			y: dimensions?.y ?? 0,
		})
	}, [onSelect, dimensions, value, smoothnessMultiplier])

	return (
		<div className="relative flex flex-row items-baseline border-2 border-primary-400 bg-primary-400/20 rounded py-2 px-1 mt-4 mx-4 ">
			<span className="text-sm">How helpful is this information?</span>
			<button
				className="absolute -top-2 -right-2 bg-primary-700 rounded-full p-1 hover:bg-primary-600 transition-colors"
				onClick={onClose}>
				<MdClose size={12} color="white" />
			</button>
			<div
				className={classnames(
					' flex flex-col items-center px-4 flex-1',
					className,
					value > 0
						? '[--focus-thumb-color:theme(colors.accent-blue/50%)]'
						: '[--focus-thumb-color:theme(colors.error/50%)]'
				)}>
				<InputWithStyledSlider
					ref={ref}
					type="range"
					id="vote"
					list="vote-ranges"
					aria-label={`Vote on helpfulness from -3 (not at all) to 3 (extremely)`}
					aria-valuemin={-3}
					aria-valuemax={3}
					aria-valuenow={Math.round(value / smoothnessMultiplier)}
					aria-valuetext={`${Math.round(
						value / smoothnessMultiplier
					)} out of ${TOTAL_VOTES} levels`}
					onTouchEnd={sendValue}
					onMouseUp={sendValue}
					onChange={e => setValue(Number(e.target.value))}
					min={-widthOfInput / 2}
					step="1"
					max={widthOfInput / 2}
					value={value}
				/>

				<div className="flex justify-between" style={{ width: `calc(100% - ${THUMB_SIZE}px)` }}>
					{Array.from({ length: TOTAL_VOTES }).map((_, index) => (
						<div key={index} className="h-2 border-l-[2px] border-primary-500 z-0" />
					))}
				</div>
				<label className="flex justify-between items-baseline text-xs w-full" htmlFor="vote">
					<span className="w-24">Not at all</span>
					<span className="text-center">?</span>
					<span className="w-24 text-right">Extremely</span>
				</label>
			</div>
		</div>
	)
}

// In order to insert the thumb image into the slider, we need to use styled-components
// and override the default styles of the input element.
const InputWithStyledSlider = styled.input.attrs({
	className:
		'w-full h-3 appearance-none bg-gradient-to-r from-error from-20% to-accent-blue to-80% border border-primary-500 cursor-pointer outline-none rounded-full ',
})`
	&::-webkit-slider-thumb {
		appearance: none;
		height: ${THUMB_SIZE}px;
		width: ${THUMB_SIZE}px;
		background-image: url(${Thumb});
		background-size: contain;
		background-position: center;
		background-repeat: no-repeat;
		border: none;
		border-radius: 50%;
		transition: 0.2s ease-in-out;
		z-index: 10;
	}

	/* Hover, active & focus Thumb: Webkit */

	&::-webkit-slider-thumb:hover {
		box-shadow: 0 0 0 2px var(--focus-thumb-color);
		background-color: var(--focus-thumb-color);
	}
	&:active::-webkit-slider-thumb {
		box-shadow: 0 0 0 4px var(--focus-thumb-color);
		background-color: var(--focus-thumb-color);
	}
	&:focus::-webkit-slider-thumb {
		box-shadow: 0 0 0 4px var(--focus-thumb-color);
		background-color: var(--focus-thumb-color);
	}
`
